import {
    IWidget,
    IVideoWidget,
    IVideoWidgetOptions,
    VideoWidgetType,
    ITextAlignment,
    ITextBlockWidget,
    IButtonWidget,
    WidgetSize,
    IGalleryWidgetImage,
    IButtons,
} from "../models/INewsArticle";
import {
    parseStringResponse,
    parseNumberResponse,
    allAreUndefined,
    parseArrayResponse,
} from "./utils";

export default function artictleContentResponseConverter(
    contentResponse: any[]
): IWidget[] | undefined {
    let content: IWidget[] = [];
    for (let i = 0; i < contentResponse.length; i++) {
        const contentItemResponse = contentResponse[i];

        const _contentItem = convertContentItemResponse(contentItemResponse);

        if (typeof _contentItem !== "undefined") content.push(_contentItem);
    }

    return content;
}

function convertContentItemResponse(contentItemResponse: any): IWidget | undefined {
    if (Array.isArray(contentItemResponse)) {
        return convertContentItemArrayResponse(contentItemResponse);
    }

    const { html, url, description } = contentItemResponse;

    const _html = parseStringResponse(html);
    const _url = parseStringResponse(url);
    const _description = parseStringResponse(description);

    if (typeof _html !== "undefined") {
        return {
            type: "html_widget",
            html: [_html],
        };
    }

    if (typeof _url !== "undefined") {
        return {
            type: "gallery_widget",
            galleryImages: [
                {
                    src: _url,
                    altText: _description,
                },
            ],
        };
    }

    return undefined;
}

function convertContentItemArrayResponse(contentItemArrayResponse: any): IWidget | undefined {
    const item = contentItemArrayResponse[0]; // only ever 1 item in the array

    const type = parseStringResponse(item.system.type);

    if (type === "youtube_video_widget")
        return {
            type,
            video: convertYoutubeWidgetResponse(item),
        };

    if (type === "text_block_widget")
        return {
            type,
            textBlock: convertTextBlockWidgetResponse(item),
        };

    if (type === "html_widget")
        return {
            type,
            html: convertHtmlWidgetResponse(item),
        };

    if (type === "button_widget")
        return {
            type,
            buttons: convertButtonsWidgetResponse(item),
        };

    if (type === "gallery_widget")
        return {
            type,
            galleryImages: convertGalleryWidgetResponse(item),
        };

    return undefined;
}

function convertYoutubeWidgetResponse(youtubeWidgetResponse: any): IVideoWidget | undefined {
    const { playerOptions, videoType, videoWidth, youtubeUrl } = youtubeWidgetResponse;

    const _width = parseNumberResponse(videoWidth);
    const _url = parseStringResponse(youtubeUrl);

    if (allAreUndefined(_url, _width)) return undefined;

    return {
        url: _url,
        options: convertYoutubeWidgetOptionsResponseResponse(playerOptions),
        type: convertYoutubeWidgetTypeResponseResponse(videoType),
    };
}

function convertYoutubeWidgetOptionsResponseResponse(
    playerOptionsResponse: any
): IVideoWidgetOptions {
    let options: IVideoWidgetOptions = {
        controls: false,
        fullscreen: false,
    };

    const parsedResponse = parseArrayResponse(playerOptionsResponse);

    if (typeof parsedResponse === "undefined" || parsedResponse.length === 0) return options;

    parsedResponse.forEach((playerOption) => {
        const { codename } = playerOption;

        if (codename === "controls") options.controls = true;
        if (codename === "fullscreen") options.fullscreen = true;
    });

    return options;
}

function convertYoutubeWidgetTypeResponseResponse(
    videoTypeResponse: any
): VideoWidgetType | undefined {
    const parsedResponse = parseArrayResponse(videoTypeResponse);
    if (typeof parsedResponse === "undefined" || parsedResponse.length === 0) return undefined;

    const type = parsedResponse[0].codename;

    if (type === "single_video" || type === "playlist") return type;

    return undefined;
}

function convertTextBlockWidgetResponse(
    textBlockWidgetResponse: any
): ITextBlockWidget | undefined {
    const { content, textAlign } = textBlockWidgetResponse;

    const _content = convertTextBlockContentResponse(content);
    const _align = convertTextBlockAlignResponse(textAlign);

    if (allAreUndefined(_content, _align)) return undefined;

    return {
        content: _content,
        align: _align,
    };
}

function convertTextBlockContentResponse(textBlockContentResponse: any): string[] | undefined {
    const parsedResponse = parseArrayResponse(textBlockContentResponse);
    if (typeof parsedResponse === "undefined" || parsedResponse.length === 0) return undefined;

    let content: string[] = [];
    parsedResponse.forEach((contentItem) => {
        const { html } = contentItem;
        const _html = parseStringResponse(html);

        if (typeof _html === "undefined") return;

        content.push(_html);
    });

    if (content.length === 0) return undefined;

    return content;
}

function convertTextBlockAlignResponse(textBlockAlignResponse: any): ITextAlignment | undefined {
    const parsedResponse = parseStringResponse(textBlockAlignResponse[0].codename);

    return getTextAlignment(parsedResponse);
}

function convertHtmlWidgetResponse(htmlWidgetResponse: any): string[] | undefined {
    const parsedResponse = parseArrayResponse(htmlWidgetResponse.code);
    if (typeof parsedResponse === "undefined" || parsedResponse.length === 0) return undefined;

    let content: string[] = [];
    parsedResponse.forEach((htmlItem) => {
        const { html } = htmlItem;

        const parsedHtml = parseStringResponse(html);

        if (typeof parsedHtml === "undefined") return;

        content.push(parsedHtml);
    });

    if (content.length === 0) return undefined;

    return content;
}

function convertButtonsWidgetResponse(buttonsWidgetResponse: any): IButtons | undefined {
    const { alignement, position, size, buttons } = buttonsWidgetResponse;

    const _direction =
        alignement.length > 0 ? parseStringResponse(alignement[0].codename) : undefined;

    return {
        buttonWidgets: convertButtonWidgetResponse(buttons),
        direction: _direction === "inline" ? "row" : "column",
        align: getAlignFromButtonResponse(position),
        size: getSizeFromButtonResponse(size),
    };
}

function convertButtonWidgetResponse(buttons: any): IButtonWidget[] | undefined {
    const parsedResponse = parseArrayResponse(buttons);
    if (typeof parsedResponse === "undefined" || parsedResponse.length === 0) return undefined;

    let content: IButtonWidget[] = [];

    parsedResponse.forEach((button) => {
        const _button: IButtonWidget = {
            href: getHrefFromButtonResponse(button[0]),
            text: parseStringResponse(button[0].text),
        };

        content.push(_button);
    });

    if (content.length === 0) return undefined;

    return content;
}

function getAlignFromButtonResponse(alignResponse: any): ITextAlignment | undefined {
    const _align =
        alignResponse.length > 0 ? parseStringResponse(alignResponse[0].codename) : undefined;

    return getTextAlignment(_align);
}

function getSizeFromButtonResponse(sizeResponse: any): WidgetSize | undefined {
    const _size = parseStringResponse(sizeResponse[0].codename);

    return getWidgetSize(_size);
}

function getHrefFromButtonResponse(button: any): string | undefined {
    const { url, file } = button;

    let _href: string | undefined;

    const parsedUrl = parseStringResponse(url);

    const parsedFile = parseArrayResponse(file);
    const parsedFileUrl = parsedFile?.length === 0 ? undefined : parseStringResponse(file[0].url);

    if (typeof parsedUrl !== "undefined") _href = parsedUrl;
    else if (typeof parsedFileUrl !== "undefined") _href = parsedFileUrl;

    return _href;
}

function convertGalleryWidgetResponse(
    galleryWidgetResponse: any
): IGalleryWidgetImage[] | undefined {
    const parsedImageArray = parseArrayResponse(galleryWidgetResponse.images);
    if (typeof parsedImageArray === "undefined" || parsedImageArray.length === 0) return undefined;

    const imageSizeArray = parseArrayResponse(galleryWidgetResponse.size);
    const _size =
        typeof imageSizeArray === "undefined"
            ? undefined
            : imageSizeArray.length === 0
            ? undefined
            : imageSizeArray[0].codename;

    let images: IGalleryWidgetImage[] = [];
    parsedImageArray.forEach((image) => {
        const { url, description } = image;
        const _image: IGalleryWidgetImage = {
            size: getWidgetSize(_size),
            altText: parseStringResponse(description),
            src: parseStringResponse(url),
        };

        images.push(_image);
    });

    if (images.length === 0) return undefined;

    return images;
}

function getWidgetSize(input?: string): WidgetSize | undefined {
    if (input === "small") return "small";
    if (input === "medium") return "medium";
    if (input === "large") return "large";

    return undefined;
}

function getTextAlignment(input?: string): ITextAlignment | undefined {
    if (input === "left") return "left";
    if (input === "right") return "right";
    if (input === "centered") return "center";
    if (input === "justified") return "justify";

    return undefined;
}
